import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Tag, X } from "lucide-react";
import { trackEvent, trackException } from "@/lib/firebase";

function parseAmount(priceText) {
  if (!priceText) return 0;
  const cleaned = String(priceText).replace(/,/g, "");
  const match = cleaned.match(/\d+(?:\.\d+)?/);
  if (!match) return 0;
  return Number(match[0]);
}

function currency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

export default function PaymentCheckoutModal({
  isOpen,
  onClose,
  serviceName,
  serviceSlug,
  packageName,
  packagePrice,
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState("");
  const [couponMessage, setCouponMessage] = useState("");

  const baseAmount = useMemo(() => parseAmount(packagePrice), [packagePrice]);
  const finalAmount = useMemo(() => {
    const value = baseAmount - discountAmount;
    return value > 0 ? value : 0;
  }, [baseAmount, discountAmount]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || "https://finease-crm.vercel.app";

  const validateBasicFields = () => {
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError("Name, email, and phone are required.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return false;
    }
    if (!/^[6-9]\d{9}$/.test(phone.replace(/\D/g, ""))) {
      setError("Please enter a valid 10-digit Indian mobile number.");
      return false;
    }
    if (baseAmount <= 0) {
      setError("This package has no valid payable amount.");
      return false;
    }
    setError("");
    return true;
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage("Enter a coupon code first.");
      trackEvent("coupon_apply_attempt", {
        service_slug: serviceSlug,
        package_name: packageName,
        reason: "empty_code",
      });
      return;
    }

    setApplyingCoupon(true);
    setCouponMessage("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/coupon/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          coupon_code: couponCode.trim(),
          service_slug: serviceSlug,
          amount: baseAmount,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setAppliedCoupon(null);
        setDiscountAmount(0);
        setCouponMessage(data.message || data.error || "Invalid coupon.");
        trackEvent("coupon_apply_failed", {
          service_slug: serviceSlug,
          package_name: packageName,
          coupon_code: couponCode.trim().toUpperCase(),
          reason: data.message || data.error || "invalid_coupon",
        });
        return;
      }

      setAppliedCoupon(data.coupon_code);
      setDiscountAmount(Number(data.discount_amount || 0));
      setCouponMessage(data.message || "Coupon applied.");
      trackEvent("coupon_applied", {
        service_slug: serviceSlug,
        package_name: packageName,
        coupon_code: data.coupon_code,
        discount_amount: Number(data.discount_amount || 0),
        final_amount: Number(data.final_amount || 0),
      });
    } catch (err) {
      setCouponMessage("Could not validate coupon right now.");
      trackException("coupon_validate_request_failed");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handlePayNow = async (e) => {
    e.preventDefault();
    if (!validateBasicFields()) {
      trackEvent("payment_initiate_blocked", {
        service_slug: serviceSlug,
        package_name: packageName,
        reason: "validation_failed",
      });
      return;
    }

    setIsPaying(true);
    trackEvent("payment_initiate_attempt", {
      service_slug: serviceSlug,
      package_name: packageName,
      amount: Number(baseAmount || 0),
      has_coupon: Boolean(appliedCoupon),
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          company: company.trim(),
          message: message.trim(),
          service_name: serviceName,
          service_slug: serviceSlug,
          package_name: packageName,
          amount: baseAmount,
          coupon_code: appliedCoupon || "",
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setError(data.message || data.error || "Unable to initiate payment.");
        trackEvent("payment_initiate_failed", {
          service_slug: serviceSlug,
          package_name: packageName,
          reason: data.message || data.error || "initiation_failed",
        });
        return;
      }

      if (data?.fields?.txnid) {
        localStorage.setItem("finease_last_txnid", String(data.fields.txnid));
      }
      trackEvent("payment_redirect_to_gateway", {
        service_slug: serviceSlug,
        package_name: packageName,
        txnid: data?.fields?.txnid || "",
        final_amount: Number(data?.fields?.amount || 0),
      });

      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.payu_url;

      Object.entries(data.fields || {}).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      setError("Unable to start payment right now.");
      trackException("payment_initiate_request_failed");
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="max-w-2xl mx-auto mt-16 md:mt-20 bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 md:p-8 relative"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 text-slate-400 hover:text-white"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <h3 className="text-2xl font-semibold text-white mb-1">Complete Payment</h3>
            <p className="text-slate-400 text-sm mb-6">{serviceName} • {packageName}</p>

            <form onSubmit={handlePayNow} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Full Name *"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="tel"
                  placeholder="Phone *"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500"
                />
                <input
                  type="text"
                  placeholder="Company (optional)"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500"
                />
              </div>

              <textarea
                rows={2}
                placeholder="Message (optional)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 resize-none"
              />

              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center">
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Coupon Code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-slate-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleApplyCoupon}
                  disabled={applyingCoupon}
                  className="border border-[#00C896]/40 text-[#00C896] px-4 py-3 rounded-lg hover:bg-[#00C896]/10 transition-colors disabled:opacity-50"
                >
                  {applyingCoupon ? "Applying..." : "Apply Coupon"}
                </button>
              </div>

              {couponMessage && (
                <p className={`text-sm ${appliedCoupon ? "text-[#00C896]" : "text-amber-400"}`}>
                  {couponMessage}
                </p>
              )}

              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>Base Amount</span>
                  <span>{currency(baseAmount)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Discount</span>
                  <span>- {currency(discountAmount)}</span>
                </div>
                <div className="flex justify-between text-white font-semibold text-base pt-1 border-t border-white/10">
                  <span>Amount Payable</span>
                  <span>{currency(finalAmount)}</span>
                </div>
              </div>

              {error && <p className="text-sm text-red-400">{error}</p>}

              <button
                type="submit"
                disabled={isPaying}
                className="w-full bg-[#00C896] text-black font-semibold py-3.5 rounded-lg hover:bg-[#00E6AC] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {isPaying ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Redirecting to PayU...
                  </>
                ) : (
                  `Pay ${currency(finalAmount)} with PayU`
                )}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
